// Work queue — one lead at a time, stage-aware. Ladder:
//   New → Contacted → Qualified → Scheduled → Quoted → Won/Lost
// Each stage shows the fields + actions that stage needs. Left card carries the
// stage context (inquiry / last message / quote / appointment) directly above
// the compose box; the right rail is persistent route context.
import React, { useCallback, useEffect, useState } from 'react';
import CustomerMatchBadge from './CustomerMatchBadge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  listCrmLeads, updateLeadStage, setLeadOutcome, spamCrmLead,
  getLeadNearby, getLeadQueueContext, getLeadPipeline,
} from '../../api/leadsApi';
import { createAppointment } from '../../api/appointmentsApi';
import { sendCommunication, createFollowUpTask } from '../../api/communications';
import MapEmbed from '../maps/MapEmbed';
import { getActiveTenant } from '../../helpers/tenantHelpers';
import './LeadWorkQueue.css';
import BrandFilter from '../common/BrandFilter';
import useBrandFilter from '../../hooks/useBrandFilter';
import { brandParams } from '../../hooks/brandFilterState';

const STAGES = [
  { key: 'NEW', label: 'New' },
  { key: 'CONTACTED', label: 'Contacted' },
  { key: 'QUALIFIED', label: 'Qualified' },
  { key: 'ESTIMATE_SCHEDULED', label: 'Scheduled' },
  { key: 'ESTIMATE_SENT', label: 'Quoted' },
  { key: 'BOOKED', label: 'Won' },
  { key: 'LOST', label: 'Lost' },
];
const NEXT_STAGE = {
  NEW: 'CONTACTED', CONTACTED: 'QUALIFIED', QUALIFIED: 'ESTIMATE_SCHEDULED',
  ESTIMATE_SCHEDULED: 'ESTIMATE_SENT', ESTIMATE_SENT: 'BOOKED',
};
const labelOf = (k) => STAGES.find((s) => s.key === k)?.label || k;
const ACTIONABLE = ['NEW', 'CONTACTED', 'QUALIFIED', 'ESTIMATE_SCHEDULED', 'ESTIMATE_SENT'];
const STAGE_KEY = 'wq.stage.v1';
const COMPOSE_STAGES = new Set(['NEW', 'CONTACTED', 'ESTIMATE_SCHEDULED', 'ESTIMATE_SENT']);

const leadName = (l) => (`${l.first_name || ''} ${l.last_name || ''}`.trim() || l.name || 'Lead');
const leadPhone = (l) => l.phone || l.phone_number || l.primary_phone || l.contact_phone || '';
const leadEmail = (l) => l.email || l.primary_email || '';
const inquiryType = (l) => (l.event_type || l.service || '').trim();
const inquiryDetail = (l) => (l.message || '').trim();
const leadAddress = (l) => [l.address, l.city, l.state, l.zip_code].filter(Boolean).join(', ');
const leadSource = (l) => (l.lead_origin || l.created_source || l.source_table || '').replace(/_/g, ' ').trim();
const firstOf = (l) => (l.first_name || leadName(l).split(' ')[0] || '').trim();
// #3: only name the type when it's a real one — never "your Other inquiry".
const namedType = (t) => (/^(other|general|n\/?a|na|unknown|none|—|-)?$/i.test((t || '').trim()) ? '' : (t || '').trim());

function relTime(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
function apptWhen(iso) {
  if (!iso) return { text: '', urgency: '' };
  const d = new Date(iso);
  const day0 = new Date(); day0.setHours(0, 0, 0, 0);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  const diff = Math.round((dd - day0) / 86400000);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(':00', '');
  let day; let urgency = '';
  if (diff === 0) { day = 'today'; urgency = 'today'; }
  else if (diff === 1) { day = 'tomorrow'; urgency = 'soon'; }
  else day = d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
  return { text: `${day} at ${time}`, urgency };
}

const starters = (first, company) => [
  `Hi ${first || 'there'}, this is ${company} — saw you reached out. What can we help you with?`,
  `Hi ${first || 'there'}, ${company} here. Got a minute this week to talk about what you're planning?`,
  `Hi ${first || 'there'}, thanks for reaching out to ${company}. What are you looking to get done?`,
];

export default function LeadWorkQueue() {
  const navigate = useNavigate();
  const [stage, setStage] = useState(() => localStorage.getItem(STAGE_KEY) || null);
  const { brands, show: brandShow, brand, setBrand } = useBrandFilter();
  const [leads, setLeads] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [reachOut, setReachOut] = useState('');
  const [sendChannel, setSendChannel] = useState('sms');
  const [followUp, setFollowUp] = useState('');
  const [nearby, setNearby] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [visitAt, setVisitAt] = useState(''); // datetime-local for booking

  // Default tab: Kyle's last, else the actionable stage with the most work.
  useEffect(() => {
    if (stage) return;
    let alive = true;
    getLeadPipeline().then((r) => {
      if (!alive) return;
      const grouped = r.data || {};
      let best = 'NEW'; let bestN = -1;
      ACTIONABLE.forEach((s) => { const n = (grouped[s] || []).length; if (n > bestN) { bestN = n; best = s; } });
      setStage(best);
    }).catch(() => { if (alive) setStage('NEW'); });
    return () => { alive = false; };
  }, [stage]);

  const pickStage = (s) => { setStage(s); localStorage.setItem(STAGE_KEY, s); };

  const load = useCallback(async (st) => {
    setLoading(true); setError(''); setIdx(0); setNote(''); setReachOut(''); setFollowUp('');
    try {
      const res = await listCrmLeads({ pipeline_stage: st, ...brandParams(brand) });
      const rows = Array.isArray(res.data?.results) ? res.data.results : (Array.isArray(res.data) ? res.data : []);
      setLeads(rows);
    } catch (e) { setError('Could not load the queue.'); setLeads([]); }
    finally { setLoading(false); }
  }, [brand]);
  useEffect(() => { if (stage) load(stage); }, [stage, load]);

  const lead = leads[idx];
  const first = lead ? firstOf(lead) : '';
  const company = (getActiveTenant()?.name || 'our team').trim();
  const hasInquiry = !!(inquiryDetail(lead || {}) || inquiryType(lead || {}));

  // Per-lead context (last message / quote / appointment) + nearby.
  useEffect(() => {
    let alive = true;
    setCtx(null); setNearby(null);
    if (!lead?.id) return () => { alive = false; };
    getLeadQueueContext(lead.id).then((r) => { if (alive) setCtx(r.data || {}); }).catch(() => { if (alive) setCtx({}); });
    getLeadNearby(lead.id).then((r) => { if (alive) setNearby(Array.isArray(r.data?.nearby) ? r.data.nearby : []); })
      .catch(() => { if (alive) setNearby([]); });
    return () => { alive = false; };
  }, [lead]);

  // Stage-appropriate compose draft — recomputed when the lead, its context, or
  // the stage changes. Empty (→ starters) when NEW has no inquiry to reference.
  useEffect(() => {
    if (!lead) { setReachOut(''); return; }
    setNote(''); setFollowUp(''); setVisitAt('');
    // Default the send channel to what the lead actually has (SMS if a phone is
    // present, else email) — the user can still switch it on the toggle.
    setSendChannel(leadPhone(lead) ? 'sms' : 'email');
    const nt = namedType(inquiryType(lead));
    if (stage === 'NEW') {
      setReachOut(hasInquiry
        ? `Hi ${first || 'there'}, thanks for reaching out to ${company}${nt ? ` about your ${nt}` : ''}. Happy to help — want to set up a quick call or visit?`
        : '');
    } else if (stage === 'CONTACTED') {
      setReachOut(`Hi ${first || 'there'}, following up from ${company} — still good to move forward? Happy to answer anything.`);
    } else if (stage === 'ESTIMATE_SCHEDULED') {
      const a = ctx?.next_appointment;
      const w = a?.start ? apptWhen(a.start).text : '';
      const at = a?.address ? ` at ${a.address}` : '';
      setReachOut(`Hi ${first || 'there'}, confirming your visit${w ? ` ${w}` : ''}${at}. Reply here if the time needs to change. — ${company}`);
    } else if (stage === 'ESTIMATE_SENT') {
      const amt = ctx?.latest_quote?.amount ? `$${ctx.latest_quote.amount} ` : '';
      setReachOut(`Hi ${first || 'there'}, following up on your ${amt}quote from ${company}. Any questions? Happy to walk through it.`);
    } else setReachOut('');
  }, [lead, ctx, stage, first, hasInquiry]);

  const advanceToNext = () => { if (idx + 1 < leads.length) setIdx(idx + 1); else setLeads([]); };
  const run = async (fn, ok, { next = true } = {}) => {
    if (!lead) return;
    setBusy(true);
    try { await fn(); toast.success(ok); if (next) advanceToNext(); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const doSend = ({ advanceStage } = {}) => {
    const body = reachOut.trim();
    if (!body) { toast.info('Nothing to send'); return; }
    const phone = leadPhone(lead);
    const email = leadEmail(lead);
    // Send on the selected channel (default follows what the lead has). Fixes the
    // former SMS-only Send, which left email-only tenants (no Twilio) unable to
    // reply from the queue at all.
    const channel = sendChannel === 'email' ? 'email' : 'sms';
    if (channel === 'sms' && !phone) { toast.error('No phone on this lead'); return; }
    if (channel === 'email' && !email) { toast.error('No email on this lead'); return; }
    run(async () => {
      const res = await sendCommunication(
        channel === 'email'
          ? { channel: 'email', contact_email: email, body, lead_id: lead.id }
          : { contact_phone: phone, body, lead_id: lead.id }
      );
      // Honor the real send outcome. The send endpoint reports failure via
      // success:false / failed[] (a 207 partial returns HTTP 200; a hard failure
      // 502 already throws). NEVER claim "Sent" or advance the lead's stage on a
      // send that did not actually go through — that was the silent-success gap.
      if (res && res.success === false) {
        const reason = res.failed?.[0]?.error || res.reason || res.error || 'message was not delivered';
        throw new Error(reason);
      }
      if (advanceStage) await updateLeadStage(lead.id, advanceStage, note ? { outcome_note: note } : {});
    }, channel === 'email' ? 'Emailed — next lead' : 'Sent — next lead');
  };
  const advance = (to, msg) => run(() => setLeadOutcome(lead.id, { stage: to, outcome_note: note || undefined }), msg || `Moved to ${labelOf(to)}`);
  const markLost = () => run(() => setLeadOutcome(lead.id, { stage: 'LOST', do_not_contact: true, outcome_note: note || 'Not interested' }), 'Marked lost — suppressed');
  const markSpam = () => run(() => spamCrmLead(lead.id), 'Removed — marked not a real lead');
  const saveNoteOnly = () => { if (!note.trim()) { toast.info('Nothing to save'); return; } run(() => setLeadOutcome(lead.id, { outcome_note: note }), 'Note saved', { next: false }); };
  const addFollowUp = () => { if (!followUp) return; run(() => createFollowUpTask({ lead: lead.id, title: `Follow up: ${leadName(lead)}`, description: note || undefined, task_type: 'follow_up', due_date: new Date(`${followUp}T09:00:00`).toISOString() }), 'Follow-up set — next lead'); };
  // Book a real estimate visit. This is the PROJECT shape's "schedule" action;
  // it DELEGATES to the canonical appointment create path (which converts the
  // lead, checks overlap, resolves offering, mirrors to Schedule, and sends the
  // booking confirmation) — no parallel create. When the workflow registry
  // lands, this call moves behind the registry's shape-resolved schedule action
  // (project=visit, recurring=series, events=date-hold+tour). Then we advance
  // the lead to Scheduled. `visitAt` is browser-local; toISOString() → UTC.
  const bookVisit = () => {
    if (!visitAt) { toast.info('Pick a date & time'); return; }
    if (!lead) return;
    setBusy(true);
    createAppointment({
      lead_id: lead.id,
      start: new Date(visitAt).toISOString(),
      duration_minutes: 60,
      service_type: inquiryType(lead) || 'Estimate visit',
      address: lead.address, city: lead.city, state: lead.state, zip_code: lead.zip_code,
    })
      .then(() => updateLeadStage(lead.id, 'ESTIMATE_SCHEDULED'))
      .then(() => { toast.success('Visit booked — confirmation sent'); advanceToNext(); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Booking failed'))
      .finally(() => setBusy(false));
  };

  const lastContact = lead ? relTime(lead.last_contacted_at || lead.last_outbound_at) : null;
  const contactedRecently = lastContact && /^(today|yesterday|1d ago|2d ago)$/.test(lastContact);
  const isEvents = ctx?.vertical === 'events';

  // ── stage context block (left, above compose) ──
  const stageContext = () => {
    if (!lead) return null;
    if (stage === 'CONTACTED') {
      const m = ctx?.last_message;
      return (
        <div className="wq-ctx">
          <span className="wq-ctx-label">Last message {m?.at ? `· ${relTime(m.at)}` : ''}</span>
          {m ? <p className="wq-ctx-body">{m.direction === 'inbound' ? 'They said: ' : 'You said: '}{m.body}</p>
            : <div className="wq-muted">No prior message on file.</div>}
        </div>
      );
    }
    if (stage === 'QUALIFIED') {
      return (
        <div className="wq-ctx">
          <span className="wq-ctx-label">Job details</span>
          {isEvents ? (
            <p className="wq-ctx-body">{[ctx?.context?.event_type, ctx?.context?.event_date && new Date(ctx.context.event_date).toLocaleDateString(), ctx?.context?.guest_count && `${ctx.context.guest_count} guests`].filter(Boolean).join(' · ') || 'No event details yet.'}</p>
          ) : (
            <p className="wq-ctx-body">{inquiryDetail(lead) || [inquiryType(lead), leadAddress(lead)].filter(Boolean).join(' · ') || 'No scope on file — add notes below.'}</p>
          )}
        </div>
      );
    }
    if (stage === 'ESTIMATE_SCHEDULED') {
      const a = ctx?.next_appointment;
      const w = a?.start ? apptWhen(a.start) : null;
      return (
        <div className="wq-ctx">
          <span className="wq-ctx-label">Scheduled visit</span>
          {a?.start ? (
            <>
              <div className={`wq-appt${w.urgency === 'today' ? ' wq-appt-today' : w.urgency === 'soon' ? ' wq-appt-soon' : ''}`}>
                {w.urgency === 'today' ? '⏰ ' : ''}{w.text}
              </div>
              {a.address ? <div className="wq-sub">{a.address}</div> : null}
              {a.service ? <div className="wq-sub">Looking at: {a.service}</div> : null}
            </>
          ) : <div className="wq-muted">No upcoming appointment found — book one in Schedule.</div>}
        </div>
      );
    }
    if (stage === 'ESTIMATE_SENT') {
      const q = ctx?.latest_quote;
      return (
        <div className="wq-ctx">
          <span className="wq-ctx-label">Quote</span>
          {q ? (
            <div className="wq-quote">
              <span className="wq-quote-amt">{q.amount ? `$${q.amount}` : '—'}</span>
              <span className="wq-sub">{q.status}{q.sent_at ? ` · sent ${relTime(q.sent_at)}` : ''}{typeof q.days_out === 'number' ? ` · ${q.days_out}d outstanding` : ''}</span>
            </div>
          ) : <div className="wq-muted">No quote on file for this lead.</div>}
        </div>
      );
    }
    if (stage === 'BOOKED' || stage === 'LOST') {
      return (
        <div className="wq-ctx">
          <span className="wq-ctx-label">{stage === 'BOOKED' ? 'Outcome' : 'Lost reason'}</span>
          <p className="wq-ctx-body">
            {stage === 'BOOKED'
              ? (ctx?.latest_quote?.amount ? `Won · $${ctx.latest_quote.amount}` : 'Won')
              : (lead.lost_reason || lead.lost_notes || 'No reason recorded.')}
          </p>
        </div>
      );
    }
    // NEW → inquiry (moved here from the rail), or starters handled in compose
    return (
      <div className="wq-ctx">
        <span className="wq-ctx-label">What they asked for</span>
        {isEvents ? (
          <p className="wq-ctx-body">{[ctx?.context?.event_type, ctx?.context?.event_date && new Date(ctx.context.event_date).toLocaleDateString(), ctx?.context?.guest_count && `${ctx.context.guest_count} guests`].filter(Boolean).join(' · ') || inquiryDetail(lead) || 'No inquiry on file.'}</p>
        ) : inquiryDetail(lead) ? (
          <p className="wq-ctx-body">{inquiryDetail(lead)}</p>
        ) : (
          <div className="wq-muted">No inquiry message — start with one of the openers below.</div>
        )}
      </div>
    );
  };

  // ── primary + secondary actions per stage ──
  const primary = () => {
    if (stage === 'NEW') return <button className="wq-btn wq-send" disabled={busy} onClick={() => doSend({ advanceStage: 'CONTACTED' })}>Send &amp; next →</button>;
    if (stage === 'CONTACTED') return <button className="wq-btn wq-send" disabled={busy} onClick={() => doSend()}>Send follow-up →</button>;
    if (stage === 'QUALIFIED') return (
      <div className="wq-book">
        <input type="datetime-local" className="wq-date wq-book-when" value={visitAt} disabled={busy}
               onChange={(e) => setVisitAt(e.target.value)} />
        <button className="wq-btn wq-send" disabled={busy || !visitAt} onClick={bookVisit}>Book visit →</button>
      </div>
    );
    if (stage === 'ESTIMATE_SCHEDULED') return <button className="wq-btn wq-send" disabled={busy} onClick={() => doSend()}>Send confirmation →</button>;
    if (stage === 'ESTIMATE_SENT') return <button className="wq-btn wq-send" disabled={busy} onClick={() => doSend()}>Send follow-up →</button>;
    return null;
  };
  const secondary = () => {
    const bits = [];
    if (stage === 'CONTACTED') bits.push(<button key="q" className="wq-btn wq-advance" disabled={busy} onClick={() => advance('QUALIFIED')}>Mark qualified →</button>);
    if (stage === 'QUALIFIED') bits.push(<button key="cq" className="wq-btn" disabled={busy} onClick={() => navigate('/quotes/create', { state: { leadId: lead.id } })}>Create quote</button>);
    if (stage === 'ESTIMATE_SCHEDULED') {
      bits.push(<button key="rs" className="wq-btn" disabled={busy} onClick={() => navigate('/schedule/day')}>Reschedule</button>);
      bits.push(<button key="adv" className="wq-btn wq-advance" disabled={busy} onClick={() => advance('ESTIMATE_SENT', 'Visit done — ready to quote')}>Visit done →</button>);
    }
    if (stage === 'ESTIMATE_SENT') {
      bits.push(<button key="acc" className="wq-btn wq-advance" disabled={busy} onClick={() => advance('BOOKED', 'Quote accepted — won')}>Mark accepted</button>);
      bits.push(<button key="dec" className="wq-btn wq-danger" disabled={busy} onClick={() => advance('LOST', 'Quote declined')}>Mark declined</button>);
    }
    if (stage === 'LOST') bits.push(<button key="ro" className="wq-btn" disabled={busy} onClick={() => advance('NEW', 'Reopened')}>Reopen</button>);
    return bits;
  };

  return (
    <div className="wq-page">
      <div className="wq-head">
        <h2>Work queue</h2>
        <BrandFilter show={brandShow} brands={brands} value={brand} onChange={setBrand} />
        <div className="wq-stages">
          {STAGES.map((s) => (
            <button key={s.key} className={`wq-tab${stage === s.key ? ' on' : ''}`} onClick={() => pickStage(s.key)}>{s.label}</button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="wq-error">{error} <button className="wq-retry" onClick={() => load(stage)}>Retry</button></div>
      ) : loading || !stage ? (
        <div className="wq-empty"><div className="wq-empty-spin" />Loading the queue…</div>
      ) : !lead ? (
        <div className="wq-empty">
          <div className="wq-empty-icon">✓</div>
          <div className="wq-empty-title">Queue clear</div>
          <div className="wq-empty-sub">Nothing left in {labelOf(stage)}. Pick another stage above to keep working.</div>
        </div>
      ) : (
        <>
          <div className="wq-progress">
            <div className="wq-progress-text">
              <span className="wq-progress-count">{idx + 1}<span className="wq-progress-of"> / {leads.length}</span></span>
              <span className="wq-progress-stage">{labelOf(stage)}</span>
            </div>
            <div className="wq-progress-track"><div className="wq-progress-fill" style={{ width: `${Math.round(((idx + 1) / leads.length) * 100)}%` }} /></div>
          </div>

          <div className="wq-grid">
            <div className="wq-card">
              <div className="wq-card-head">
                <div className="wq-who">
                  <div className="wq-name">{leadName(lead)}</div>
                  <CustomerMatchBadge lead={lead} />
                  <div className="wq-contact">
                    {leadPhone(lead) ? <span className="wq-mono">{leadPhone(lead)}</span> : null}
                    {leadEmail(lead) ? <span className="wq-email">{leadEmail(lead)}</span> : null}
                  </div>
                </div>
                {inquiryType(lead) ? <div className="wq-tag">{inquiryType(lead)}</div> : null}
              </div>

              {contactedRecently ? <div className="wq-warn">⚠ Contacted {lastContact} — check the thread before texting again.</div> : null}

              {stageContext()}

              {COMPOSE_STAGES.has(stage) ? (
                <div className="wq-primary-zone">
                  <span className="wq-label">{stage === 'ESTIMATE_SCHEDULED' ? 'Confirmation' : 'Message'}</span>
                  {(leadPhone(lead) || leadEmail(lead)) ? (
                    <div className="wq-channel-toggle">
                      {leadPhone(lead) ? (
                        <button type="button" className={`wq-chan ${sendChannel === 'sms' ? 'active' : ''}`} disabled={busy}
                                onClick={() => setSendChannel('sms')}>SMS</button>
                      ) : null}
                      {leadEmail(lead) ? (
                        <button type="button" className={`wq-chan ${sendChannel === 'email' ? 'active' : ''}`} disabled={busy}
                                onClick={() => setSendChannel('email')}>Email</button>
                      ) : null}
                    </div>
                  ) : null}
                  <textarea className="wq-textarea" rows={3} value={reachOut} disabled={busy}
                            placeholder={(stage === 'NEW' && !hasInquiry) ? `Write your first message to ${first || 'this lead'}…` : ''}
                            onChange={(e) => setReachOut(e.target.value)} />
                  {stage === 'NEW' && !hasInquiry ? (
                    <div className="wq-starters">
                      <span className="wq-starters-label">No inquiry on file — start with:</span>
                      {starters(first, company).map((s, i) => (
                        <button key={i} className="wq-starter" disabled={busy} onClick={() => setReachOut(s)}>{s.length > 60 ? `${s.slice(0, 58)}…` : s}</button>
                      ))}
                    </div>
                  ) : null}
                  <div className="wq-primary-row">{primary()}{secondary()}</div>
                </div>
              ) : (
                <div className="wq-primary-row wq-primary-noncompose">{primary()}{secondary()}</div>
              )}

              <div className="wq-secondary">
                <textarea className="wq-textarea wq-note" rows={2} value={note} disabled={busy}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Outcome note — applies to the stage change, follow-up, or saved on its own" />
                <div className="wq-advance-row">
                  <button className="wq-btn wq-ghost" disabled={busy || !note.trim()} onClick={saveNoteOnly}>Save note</button>
                  <div className="wq-followup">
                    <input type="date" className="wq-date" value={followUp} disabled={busy} onChange={(e) => setFollowUp(e.target.value)} />
                    <button className="wq-btn" disabled={busy || !followUp} onClick={addFollowUp}>Set follow-up</button>
                  </div>
                </div>
              </div>

              <div className="wq-footer">
                <div className="wq-footer-left">
                  {stage !== 'LOST' ? <button className="wq-btn wq-danger" disabled={busy} onClick={markLost}>Mark lost</button> : null}
                  {stage === 'NEW' ? <button className="wq-btn wq-spam" disabled={busy} onClick={markSpam}>Not a real lead</button> : null}
                </div>
                <button className="wq-btn wq-skip" disabled={busy} onClick={advanceToNext}>Skip for now →</button>
              </div>
            </div>

            <aside className="wq-rail">
              <section className="wq-panel">
                <span className="wq-panel-label">Property</span>
                {leadAddress(lead) ? (<><MapEmbed address={leadAddress(lead)} height={190} /><div className="wq-address">{leadAddress(lead)}</div></>)
                  : <div className="wq-muted">No address on this lead.</div>}
              </section>
              <section className="wq-panel wq-panel-row">
                <div><span className="wq-panel-label">Source</span><div className="wq-fact">{leadSource(lead) || '—'}</div></div>
                <div><span className="wq-panel-label">Age</span><div className="wq-fact">{relTime(lead.created_at) || '—'}</div></div>
              </section>
              <section className="wq-panel">
                <span className="wq-panel-label">Prior contact</span>
                {lastContact ? (
                  <div className={`wq-fact${contactedRecently ? ' wq-fact-warn' : ''}`}>Last reached out {lastContact}{lead.contact_attempts ? ` · ${lead.contact_attempts} attempt${lead.contact_attempts > 1 ? 's' : ''}` : ''}</div>
                ) : <div className="wq-muted">Never contacted.</div>}
                {lead.last_inbound_at ? <div className="wq-sub">They last replied {relTime(lead.last_inbound_at)}</div> : null}
                {lead.utm_campaign ? <div className="wq-sub">Campaign: {lead.utm_campaign}</div> : null}
              </section>
              <section className="wq-panel">
                <span className="wq-panel-label">Nearby customers</span>
                {nearby === null ? <div className="wq-muted">Checking the area…</div>
                  : nearby.length === 0 ? <div className="wq-muted">None within range.</div>
                    : <ul className="wq-nearby">{nearby.slice(0, 5).map((n) => (<li key={n.id}><span className="wq-nearby-name">{n.name}</span><span className="wq-nearby-dist">{n.distance_mi} mi</span></li>))}</ul>}
              </section>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
